/**
 * "What this team needs" on a demo team page (#2824), as illustrated tiles.
 *
 * Each need sentence from `GET /api/demo/scenarios` gets a short label and a
 * small inline SVG vignette in the demo art palette (the cream, mint and
 * teal of `/site/characters`). The sentence itself is shown word for word.
 * A sentence with no entry in `NEED_TILES` falls back on its anchor.
 * Every vignette is `aria-hidden`.
 */
import type { DemoConsideration } from "./demoScenarios";
import "./demo-chapter-art.css";

const C = {
  cream: "#f2efe8",
  line: "#d9d4c8",
  ink: "#173d3c",
  deep: "#2f5a57",
  mint: "#a8dccb",
  teal: "#74b7a2",
  coral: "#ec8a7c",
  gold: "#f6dc9a",
  goldLine: "#c9b27a",
  skin: "#e2ad8d",
  white: "#fbfcf9",
} as const;

const HAIR = ["#2b2320", "#6b4a2e", "#8a522f"] as const;

function Art({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 160 100" className="dc-tneed-svg" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

/** A head-and-shoulders figure standing on `y`. */
function Bust({
  x,
  y,
  s = 1,
  body = C.teal,
  hair = 0,
}: { x: number; y: number; s?: number; body?: string; hair?: number }) {
  const r = 7 * s;
  const hy = y - 21 * s;
  return (
    <g>
      <path
        d={`M${x - 11 * s} ${y}v${-6 * s}a${11 * s} ${7 * s} 0 0 1 ${22 * s} 0v${6 * s}z`}
        fill={body}
      />
      <circle cx={x} cy={hy} r={r} fill={C.skin} />
      <path d={`M${x - r} ${hy}a${r} ${r} 0 0 1 ${2 * r} 0z`} fill={HAIR[hair % HAIR.length]} />
    </g>
  );
}

function Moon({
  cx,
  cy,
  r = 6,
  fill = C.cream,
}: { cx: number; cy: number; r?: number; fill?: string }) {
  const k = r * 1.35;
  return (
    <path
      d={`M${cx + r * 0.3} ${cy - r}a${r} ${r} 0 0 0 0 ${2 * r}a${k} ${k} 0 0 1 0 ${-2 * r}z`}
      fill={fill}
    />
  );
}

function Sun({ cx, cy, r = 6 }: { cx: number; cy: number; r?: number }) {
  return <circle cx={cx} cy={cy} r={r} fill={C.gold} stroke={C.goldLine} strokeWidth={1.5} />;
}

function starPoints(cx: number, cy: number, r: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r * 0.45;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    pts.push(`${(cx + rad * Math.cos(a)).toFixed(1)},${(cy + rad * Math.sin(a)).toFixed(1)}`);
  }
  return pts.join(" ");
}

const FIVE = [0, 1, 2, 3, 4] as const;
const SEVEN = [0, 1, 2, 3, 4, 5, 6] as const;
const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"] as const;

/** Day posts in a row, with night cover under each. */
function StationsArt() {
  return (
    <Art>
      {FIVE.map((i) => (
        <g key={i}>
          <rect
            x={8 + i * 30}
            y={12}
            width={24}
            height={32}
            rx={6}
            fill={C.cream}
            stroke={C.line}
          />
          <Sun cx={20 + i * 30} cy={28} r={5} />
          <rect x={8 + i * 30} y={54} width={24} height={32} rx={6} fill={C.deep} />
          <Moon cx={19 + i * 30} cy={70} r={6} />
        </g>
      ))}
    </Art>
  );
}

/** A crew line-up with the lead in the middle under a star. */
function CrewArt() {
  const crew = [
    { x: 28, s: 0.85, body: C.mint },
    { x: 54, s: 1, body: C.teal },
    { x: 80, s: 1.25, body: C.deep },
    { x: 106, s: 1, body: C.teal },
    { x: 132, s: 0.85, body: C.mint },
  ];
  return (
    <Art>
      {crew.map((p, i) => (
        <Bust key={p.x} x={p.x} y={92} s={p.s} body={p.body} hair={i} />
      ))}
      <polygon points={starPoints(80, 34, 8)} fill={C.gold} stroke={C.goldLine} />
    </Art>
  );
}

/** One senior at a post, with a star on the shoulder. */
function SeniorArt() {
  return (
    <Art>
      <rect x={46} y={12} width={68} height={56} rx={8} fill={C.cream} stroke={C.line} />
      <rect x={54} y={20} width={52} height={6} rx={3} fill={C.mint} />
      <Bust x={80} y={96} s={1.35} body={C.deep} hair={1} />
      <polygon points={starPoints(97, 78, 7)} fill={C.gold} stroke={C.goldLine} />
    </Art>
  );
}

/** A moon over a bed. */
function RestArt() {
  return (
    <Art>
      <Moon cx={36} cy={26} r={12} fill={C.gold} />
      <text x={92} y={38} className="dc-tneed-svg-text" fontSize={13} fill={C.deep}>
        z
      </text>
      <text x={102} y={28} className="dc-tneed-svg-text" fontSize={10} fill={C.deep}>
        z
      </text>
      <rect x={34} y={50} width={8} height={40} rx={3} fill={C.deep} />
      <rect x={34} y={76} width={100} height={8} rx={3} fill={C.deep} />
      <rect x={128} y={76} width={6} height={14} rx={2} fill={C.deep} />
      <rect x={40} y={64} width={92} height={13} rx={6} fill={C.cream} stroke={C.line} />
      <ellipse cx={56} cy={62} rx={11} ry={6} fill={C.white} stroke={C.line} />
      <circle cx={58} cy={56} r={7} fill={C.skin} />
      <path d="M51 56a7 7 0 0 1 14 0z" fill={HAIR[0]} />
      <rect x={66} y={57} width={64} height={17} rx={8} fill={C.teal} />
    </Art>
  );
}

/** Three people, three equal stacks of nights, one level line. */
function FairArt() {
  const xs = [36, 80, 124];
  const bodies = [C.teal, C.deep, C.mint];
  return (
    <Art>
      <path d="M18 21H142" stroke={C.coral} strokeWidth={2.5} strokeDasharray="5 4" />
      {xs.map((x, i) => (
        <g key={x}>
          {[0, 1, 2].map((j) => (
            <g key={j}>
              <rect x={x - 12} y={26 + j * 14} width={24} height={12} rx={4} fill={C.deep} />
              <Moon cx={x} cy={32 + j * 14} r={4} />
            </g>
          ))}
          <Bust x={x} y={98} s={0.8} body={bodies[i] ?? C.teal} hair={i} />
        </g>
      ))}
    </Art>
  );
}

/** Full-time and part-time, each with a share that fits their hours. */
function PartTimeArt() {
  const rows = [
    { y: 46, filled: 6, body: C.deep },
    { y: 88, filled: 3, body: C.mint },
  ];
  return (
    <Art>
      {rows.map((row, r) => (
        <g key={row.y}>
          <Bust x={24} y={row.y} s={0.85} body={row.body} hair={r + 1} />
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <rect
              key={i}
              x={46 + i * 18}
              y={row.y - 22}
              width={14}
              height={16}
              rx={4}
              fill={i < row.filled ? C.teal : C.cream}
              stroke={i < row.filled ? "none" : C.line}
              strokeDasharray={i < row.filled ? undefined : "3 2"}
            />
          ))}
        </g>
      ))}
    </Art>
  );
}

/** Two shift cards trading places under the lead's shield. */
function SwapsArt() {
  return (
    <Art>
      <rect x={10} y={24} width={52} height={58} rx={8} fill={C.cream} stroke={C.line} />
      <rect x={17} y={31} width={38} height={8} rx={4} fill={C.gold} />
      <Bust x={36} y={78} s={0.8} body={C.teal} hair={0} />
      <rect x={98} y={24} width={52} height={58} rx={8} fill={C.cream} stroke={C.line} />
      <rect x={105} y={31} width={38} height={8} rx={4} fill={C.deep} />
      <Bust x={124} y={78} s={0.8} body={C.coral} hair={2} />
      <g fill="none" stroke={C.ink} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M64 30Q80 10 96 30" />
        <path d="M89 29l7 1 1-7" />
        <path d="M96 76Q80 96 64 76" />
        <path d="M71 77l-7-1-1 7" />
      </g>
      <path d="M80 41l9 3v7c0 6-4 9-9 11c-5-2-9-5-9-11v-7z" fill={C.deep} />
      <path
        d="M76 51l3 3 5-6"
        fill="none"
        stroke={C.cream}
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Art>
  );
}

/** A week strip: lit days, an accent day, or three bands a day. */
function WeekArt({
  lit,
  accent,
  bands = false,
}: { lit: readonly number[]; accent?: number; bands?: boolean }) {
  return (
    <Art>
      {SEVEN.map((i) => {
        const x = 5 + i * 22;
        const on = lit.includes(i);
        return (
          <g key={i}>
            <text
              x={x + 9}
              y={22}
              textAnchor="middle"
              className="dc-tneed-svg-text"
              fontSize={10}
              fill={C.deep}
            >
              {DAY_LETTERS[i]}
            </text>
            {bands ? (
              [C.gold, C.teal, C.deep].map((band, b) => (
                <rect key={band} x={x} y={30 + b * 15} width={18} height={13} rx={4} fill={band} />
              ))
            ) : (
              <rect
                x={x}
                y={30}
                width={18}
                height={44}
                rx={5}
                fill={i === accent ? C.coral : on ? C.teal : C.cream}
                stroke={i === accent || on ? "none" : C.line}
                strokeDasharray={i === accent || on ? undefined : "3 2"}
              />
            )}
            {i === accent ? <Sun cx={x + 9} cy={46} r={5} /> : null}
          </g>
        );
      })}
      {bands ? null : <path d="M5 86H157" stroke={C.line} strokeWidth={2} strokeLinecap="round" />}
    </Art>
  );
}

/** Seven slots, a stop line after the fifth. */
function CapArt() {
  return (
    <Art>
      {SEVEN.map((i) => {
        const x = 10 + i * 20;
        const on = i < 5;
        return (
          <g key={i}>
            <rect
              x={x}
              y={42}
              width={16}
              height={34}
              rx={5}
              fill={on ? C.teal : C.cream}
              stroke={on ? "none" : C.line}
              strokeDasharray={on ? undefined : "3 2"}
            />
            {on ? (
              <path
                d={`M${x + 4} ${59}l3 3 5-6`}
                fill="none"
                stroke={C.white}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null}
          </g>
        );
      })}
      <path d="M108 34V84" stroke={C.coral} strokeWidth={3} strokeLinecap="round" />
      <circle cx={108} cy={22} r={9} fill={C.coral} />
      <text
        x={108}
        y={26}
        textAnchor="middle"
        className="dc-tneed-svg-text"
        fontSize={11}
        fill={C.white}
      >
        5
      </text>
    </Art>
  );
}

/** A person beside a trained-skill rosette. */
function SkillArt() {
  return (
    <Art>
      <Bust x={56} y={96} s={1.3} body={C.teal} hair={2} />
      <path d="M96 52l-8 30 10-5 6 8 4-30z" fill={C.coral} />
      <path d="M116 52l8 30-10-5-6 8-4-30z" fill={C.coral} />
      <circle cx={106} cy={42} r={18} fill={C.gold} stroke={C.goldLine} strokeWidth={2} />
      <polygon points={starPoints(106, 42, 9)} fill={C.deep} />
    </Art>
  );
}

/** A licence card and a forklift. */
function LicenceArt() {
  return (
    <Art>
      <rect x={8} y={22} width={78} height={54} rx={7} fill={C.cream} stroke={C.line} />
      <rect x={16} y={31} width={24} height={30} rx={4} fill={C.mint} />
      <Bust x={28} y={61} s={0.6} body={C.deep} hair={0} />
      <rect x={46} y={34} width={32} height={5} rx={2.5} fill={C.line} />
      <rect x={46} y={45} width={24} height={5} rx={2.5} fill={C.line} />
      <rect x={46} y={56} width={28} height={5} rx={2.5} fill={C.teal} />
      <rect x={98} y={26} width={5} height={56} rx={2} fill={C.deep} />
      <rect x={86} y={78} width={16} height={4} rx={1} fill={C.deep} />
      <rect x={87} y={62} width={13} height={15} rx={2} fill={C.coral} />
      <rect
        x={112}
        y={38}
        width={20}
        height={20}
        rx={3}
        fill="none"
        stroke={C.deep}
        strokeWidth={3}
      />
      <rect x={104} y={56} width={36} height={22} rx={5} fill={C.gold} stroke={C.goldLine} />
      <circle cx={112} cy={82} r={7} fill={C.ink} />
      <circle cx={132} cy={82} r={7} fill={C.ink} />
      <circle cx={112} cy={82} r={2.5} fill={C.cream} />
      <circle cx={132} cy={82} r={2.5} fill={C.cream} />
    </Art>
  );
}

/** One person waiting at home, one waiting on site. */
function StandbyArt() {
  return (
    <Art>
      <path d="M12 54L42 30L72 54V88H12Z" fill={C.cream} stroke={C.line} strokeLinejoin="round" />
      <path
        d="M8 56L42 28L76 56"
        fill="none"
        stroke={C.coral}
        strokeWidth={4}
        strokeLinecap="round"
      />
      <Bust x={42} y={88} s={0.95} body={C.teal} hair={1} />
      <rect x={92} y={30} width={58} height={58} rx={4} fill={C.cream} stroke={C.line} />
      <rect x={88} y={24} width={66} height={9} rx={3} fill={C.deep} />
      <Bust x={121} y={88} s={0.95} body={C.deep} hair={0} />
      <circle cx={81} cy={16} r={9} fill={C.white} stroke={C.deep} strokeWidth={2} />
      <path d="M81 11v5l3 2" fill="none" stroke={C.deep} strokeWidth={2} strokeLinecap="round" />
    </Art>
  );
}

/** A calendar page with a day held, and a packed case. */
function LeaveArt() {
  return (
    <Art>
      <rect x={14} y={20} width={68} height={66} rx={7} fill={C.cream} stroke={C.line} />
      <rect x={14} y={20} width={68} height={15} rx={7} fill={C.coral} />
      <rect x={14} y={28} width={68} height={7} fill={C.coral} />
      {[0, 1, 2].map((r) =>
        [0, 1, 2].map((c) => (
          <rect
            key={`${r}-${c}`}
            x={22 + c * 19}
            y={42 + r * 14}
            width={14}
            height={10}
            rx={3}
            fill={r === 1 && c === 1 ? C.coral : C.mint}
          />
        )),
      )}
      <Sun cx={140} cy={22} r={8} />
      <path
        d="M110 50v-8a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v8"
        fill="none"
        stroke={C.deep}
        strokeWidth={4}
      />
      <rect x={96} y={50} width={50} height={36} rx={7} fill={C.teal} />
      <rect x={109} y={50} width={5} height={36} fill={C.deep} opacity={0.35} />
      <rect x={128} y={50} width={5} height={36} fill={C.deep} opacity={0.35} />
      <circle cx={104} cy={90} r={3} fill={C.ink} />
      <circle cx={138} cy={90} r={3} fill={C.ink} />
    </Art>
  );
}

/** A long flight, then two days on the ground. */
function GapArt() {
  return (
    <Art>
      <path
        d="M10 26V18H150V26"
        fill="none"
        stroke={C.coral}
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={10 + i * 48}
          y={34}
          width={44}
          height={54}
          rx={8}
          fill={i === 0 ? C.deep : C.cream}
          stroke={i === 0 ? "none" : C.line}
        />
      ))}
      <g fill={C.cream}>
        <path d="M14 66L42 54Q48 52 46 56L20 70Z" />
        <path d="M27 60L22 46H28L36 57Z" />
        <path d="M17 66L13 58H17L22 63Z" />
      </g>
      <Moon cx={80} cy={56} r={8} fill={C.teal} />
      <Moon cx={128} cy={56} r={8} fill={C.teal} />
      <circle cx={80} cy={18} r={9} fill={C.coral} />
      <text
        x={80}
        y={22}
        textAnchor="middle"
        className="dc-tneed-svg-text"
        fontSize={11}
        fill={C.white}
      >
        3
      </text>
    </Art>
  );
}

/** A control-room screen, half day, half night, with two officers. */
function ControlArt() {
  return (
    <Art>
      <rect x={36} y={10} width={88} height={58} rx={6} fill={C.deep} />
      <rect x={42} y={16} width={38} height={46} rx={3} fill="#fdf3d8" />
      <rect x={80} y={16} width={38} height={46} rx={3} fill={C.ink} />
      <Sun cx={61} cy={39} r={8} />
      <Moon cx={98} cy={39} r={9} />
      <rect x={74} y={68} width={12} height={10} fill={C.deep} />
      <rect x={60} y={78} width={40} height={5} rx={2.5} fill={C.deep} />
      <Bust x={22} y={98} s={0.85} body={C.teal} hair={0} />
      <Bust x={138} y={98} s={0.85} body={C.mint} hair={2} />
    </Art>
  );
}

const ARTS = {
  stations: StationsArt,
  crew: CrewArt,
  senior: SeniorArt,
  rest: RestArt,
  fair: FairArt,
  parttime: PartTimeArt,
  swaps: SwapsArt,
  "set-days": () => <WeekArt lit={[0, 1, 2]} />,
  saturday: () => <WeekArt lit={[0, 1, 2, 3, 4]} accent={5} />,
  "seven-days": () => <WeekArt lit={SEVEN} bands />,
  cap: CapArt,
  skill: SkillArt,
  licence: LicenceArt,
  standby: StandbyArt,
  leave: LeaveArt,
  gap: GapArt,
  control: ControlArt,
} satisfies Record<string, () => React.JSX.Element>;

export type NeedArtKey = keyof typeof ARTS;

/** Label and art for each need sentence the demo teams carry, word for word. */
export const NEED_TILES: Record<string, { label: string; art: NeedArtKey }> = {
  // Meridian General, Emergency Medicine
  "Ten stations run, with night cover every night.": { label: "Ten stations", art: "stations" },
  "Each station needs enough senior staff.": { label: "Senior cover", art: "senior" },
  "Part-time staff get a fair share for the hours they work.": {
    label: "Part-time share",
    art: "parttime",
  },
  "Night call is shared fairly within each level.": { label: "Shared nights", art: "fair" },
  "Staff swap shifts under a policy the lead sets.": { label: "Swaps", art: "swaps" },
  // Northgate Packaging, Line 2
  "Morning and afternoon each need a shift lead, a technician and two operators.": {
    label: "Crew per shift",
    art: "crew",
  },
  "Every night has a rest day after it.": { label: "Rest after nights", art: "rest" },
  "Night points are levelled within each level.": { label: "Even nights", art: "fair" },
  "One person on each shift holds a forklift licence.": {
    label: "Licensed driver",
    art: "licence",
  },
  "Leave and a course are respected.": { label: "Leave kept", art: "leave" },
  // Airline cabin crew base
  "Each London flight needs an In-flight Manager, a Chief Steward and five crew.": {
    label: "Full crew",
    art: "crew",
  },
  "Crew who fly a long-haul rest the next day.": { label: "Rest after flying", art: "rest" },
  "No one flies a long-haul twice in 3 days.": { label: "Gap between flights", art: "gap" },
  "Long-haul points are levelled within each grade.": { label: "Even long-hauls", art: "fair" },
  "Two crew wait on airport standby and two at home.": { label: "Standby", art: "standby" },
  "Leave, a wedding and courses are respected.": { label: "Leave kept", art: "leave" },
  // Bayview family clinics
  "Every clinic session has a doctor and a nurse.": { label: "Doctor and nurse", art: "crew" },
  "The main clinic always has a senior doctor.": { label: "Senior doctor", art: "senior" },
  "Two part-time staff work set days only.": { label: "Set days", art: "set-days" },
  "A Saturday morning clinic runs every week.": { label: "Saturday clinic", art: "saturday" },
  "Leave is limited to one doctor and two nurses.": { label: "Leave limit", art: "leave" },
  // Harbourline Security, campus and small site
  "The control room has a supervisor, senior officer and officer, day and night.": {
    label: "Control room",
    art: "control",
  },
  "The control room has a lead and an officer, day and night.": {
    label: "Control room",
    art: "control",
  },
  "The duty supervisor on call rests the next day.": { label: "Rest after call", art: "rest" },
  "Call points are levelled within each level.": { label: "Even call", art: "fair" },
  "Two staff stand by on site and two at home.": { label: "Standby", art: "standby" },
  "One person stands by on site each day.": { label: "Standby", art: "standby" },
  "Leave and courses are respected.": { label: "Leave kept", art: "leave" },
  // Kallang contact centre
  "Every shift has a team lead and agents.": { label: "Lead on every shift", art: "crew" },
  "Early, day and late shifts run seven days a week.": { label: "Seven days", art: "seven-days" },
  "Only billing-trained agents can take the billing queue.": {
    label: "Billing trained",
    art: "skill",
  },
  "Everyone works at most five shifts a week.": { label: "Five a week", art: "cap" },
  "Leave is limited to one lead and two agents.": { label: "Leave limit", art: "leave" },
  // Larkspur Ward 7 nursing
  "The day shift needs a nurse lead, two senior nurses and two staff nurses.": {
    label: "Day shift mix",
    art: "crew",
  },
  "Every night has a nurse in charge, and each nurse rests the next day.": {
    label: "Rest after nights",
    art: "rest",
  },
  "Two part-time nurses work Monday to Wednesday only.": { label: "Set days", art: "set-days" },
  // Saffron Lane kitchen
  "Every service has a head chef or chef de partie on the pass.": {
    label: "Senior on the pass",
    art: "senior",
  },
  "Grill and wok need trained cooks.": { label: "Trained cooks", art: "skill" },
  "Doubles are capped at five a week.": { label: "Doubles capped", art: "cap" },
  "Only one senior is on leave at a time.": { label: "One senior away", art: "leave" },
};

/** Fallback for a sentence not in `NEED_TILES`, by its Setup anchor. */
const ANCHOR_TILES: Record<string, { label: string; art: NeedArtKey }> = {
  stations: { label: "Stations", art: "stations" },
  "level-floors": { label: "Right mix", art: "crew" },
  "post-call-rest": { label: "Rest", art: "rest" },
  "call-points": { label: "Even share", art: "fair" },
  "call-balance": { label: "Even share", art: "fair" },
  "fte-fairness": { label: "Fair hours", art: "parttime" },
  swaps: { label: "Swaps", art: "swaps" },
  standby: { label: "Standby", art: "standby" },
  "leave-quota": { label: "Leave", art: "leave" },
  "control-room": { label: "Control room", art: "control" },
};

export function needTile(item: DemoConsideration): { label: string; art: NeedArtKey } {
  return NEED_TILES[item.text] ?? ANCHOR_TILES[item.anchor] ?? { label: "Need", art: "crew" };
}

/** The needs as tiles: the first is the focal one and runs full width. */
export function DemoNeedTiles({ considerations }: { considerations: DemoConsideration[] }) {
  return (
    <ul className="dc-tneeds" data-testid="demo-scenario-considerations">
      {considerations.map((item, index) => {
        const tile = needTile(item);
        const Vignette = ARTS[tile.art];
        return (
          <li
            key={`${index}-${item.anchor}`}
            className={index === 0 ? "dc-tneed dc-tneed--focal" : "dc-tneed"}
            data-testid="demo-need-tile"
            data-art={tile.art}
          >
            <div className="dc-tneed-art">
              <Vignette />
            </div>
            <div className="dc-tneed-copy">
              <p className="dc-need-label">{tile.label}</p>
              <p className="dc-need-text">{item.text}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
